import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { getOrganizations, Organization } from '../../services/api/organizations';
import { auth } from "../../firebase-config";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Badge } from 'components/ui/badge';
import SvgSpinner from 'components/Spinners/SvgSpinner';
import { toast } from 'sonner';
import { Copy, Check, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Key } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from 'components/ui/alert-dialog';
import { getAllUsers } from 'services/api/user';

interface ConnectorKey {
  key: string;
  organizationId: string;
  buildingId?: string;
  description?: string;
  expiresAt?: string;
  allowedIPs?: string[];
  createdBy: string;
  createdAt: { seconds: number; nanoseconds: number } | string | number | Date;
  revoked: boolean;
  revokedAt?: { seconds: number; nanoseconds: number } | string | number | Date | null;
}

interface UserInfo {
  uid: string;
  displayName?: string;
  email?: string;
  role?: 'admin' | 'user';
}

type SortKey = 'key' | 'description' | 'createdBy' | 'createdAt' | 'expiresAt' | 'revoked';
type SortDirection = 'asc' | 'desc';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getAuthHeader = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated");
  }
  const token = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const ConnectorKeysPage: React.FC = () => {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [keys, setKeys] = useState<ConnectorKey[]>([]);
  const [userInfoCache, setUserInfoCache] = useState<Record<string, UserInfo>>({});
  const [receivedData, setReceivedData] = useState<any[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingKey, setRevokingKey] = useState<string | null>(null);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    organizationId: '',
    buildingId: '',
    description: '',
    expiresAt: '',
    allowedIPs: ''
  });
  
  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Calculate total pages for pagination
  const totalPages = Math.ceil(keys.length / itemsPerPage);

  useEffect(() => {
    fetchOrganizations();
    fetchAllUsers(); // Fetch all users once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      fetchKeys();
      fetchReceivedData();
    } else {
      setKeys([]);
      setReceivedData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  // Handle sorting
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const orgs = await getOrganizations();
      setOrganizations(orgs);
      // Auto-select the first organization
      if (orgs.length > 0 && !selectedOrgId) {
        setSelectedOrgId(orgs[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const users = await getAllUsers();
      const userMap = users.reduce((acc, user) => {
        acc[user.uid] = {
          uid: user.uid,
          displayName: user.displayName || user.email,
          email: user.email,
          role: user.role,
        };
        return acc;
      }, {} as Record<string, UserInfo>);
      setUserInfoCache(userMap);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
      toast.error('Could not load user data for roles.');
    }
  };

  const fetchUserInfo = async (uid: string): Promise<UserInfo> => {
    if (userInfoCache[uid]) {
      return userInfoCache[uid];
    }

    try {
      const headers = await getAuthHeader();
      const res = await fetch(`${API_BASE_URL}/api/v1/users/${uid}`, {
        headers,
      });
      if (res.ok) {
        const userData = await res.json();
        const userInfo = {
          uid,
          displayName: userData.displayName || userData.email,
          email: userData.email,
        };
        setUserInfoCache(prev => ({ ...prev, [uid]: userInfo }));
        return userInfo;
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }

    // Fallback to UID if user info not found
    return { uid, displayName: uid, email: '' };
  };

  const fetchKeys = async () => {
    if (!selectedOrgId) return;
    
    setLoadingKeys(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`${API_BASE_URL}/api/v1/connector-keys?organizationId=${selectedOrgId}`, {
        headers,
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch keys');
      }
      const data = await res.json();
      setKeys(data);

      // Fetch user info for all unique createdBy UIDs
      const uniqueUids = Array.from(new Set(data.map((key: ConnectorKey) => key.createdBy)));
      const uidsToFetch = uniqueUids.filter(uid => !userInfoCache[uid as string]);
      
      if (uidsToFetch.length > 0) {
        for (const uid of uidsToFetch) {
          await fetchUserInfo(uid as string);
        }
      }
    } catch (error) {
      console.error('Failed to fetch keys:', error);
      toast.error('Failed to load connector keys.');
    } finally {
      setLoadingKeys(false);
    }
  };

  const formatDate = (ts: any) => {
    try {
      if (!ts) return 'Never';
      if (typeof ts === 'object' && typeof ts._seconds === 'number') {
        const d = new Date(ts._seconds * 1000);
        return d.toLocaleString();
      }
      if (typeof ts === 'object' && typeof ts.seconds === 'number') {
        const d = new Date(ts.seconds * 1000);
        return d.toLocaleString();
      }
      if (typeof ts?.toDate === 'function') {
        const d = ts.toDate();
        return d.toLocaleString();
      }
      const d = new Date(ts);
      if (isNaN(d.getTime())) {
        console.warn('Invalid date value:', ts);
        return 'Invalid Date';
      }
      return d.toLocaleString();
    } catch (error) {
      console.warn('formatDate error for value:', ts, error);
      return 'Invalid Date';
    }
  };

  const fetchReceivedData = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await fetch(`${API_BASE_URL}/api/v1/connector-keys/data`, {
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        const filtered = Array.isArray(data)
          ? data.filter((item: any) => !selectedOrgId || item.organizationId === selectedOrgId)
          : [];
        setReceivedData(filtered);
      } else {
        toast.error('Failed to fetch received data.');
        console.error('Failed to fetch received data');
      }
    } catch (error) {
      toast.error('An error occurred while fetching connector data.');
      console.error('Error fetching received data:', error);
    }
  };

  const handleCreateKey = async () => {
    if (!createForm.organizationId) {
      toast.warning('Per favore seleziona prima uno studio');
      return;
    }

    if (createForm.allowedIPs && createForm.allowedIPs.trim()) {
      const ipAddresses = createForm.allowedIPs.split(',').map(ip => ip.trim()).filter(ip => ip);
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      for (const ip of ipAddresses) {
        if (!ipRegex.test(ip)) {
          toast.error(`Invalid IP address format: ${ip}`);
          return;
        }
      }
    }

    setCreating(true);
    try {
      const headers = await getAuthHeader();
      const user = auth.currentUser;
      const createdBy = user?.uid || 'unknown';
      
      const requestBody = {
        organizationId: createForm.organizationId,
        buildingId: createForm.buildingId === 'none' ? undefined : createForm.buildingId,
        description: createForm.description || undefined,
        expiresAt: createForm.expiresAt || undefined,
        allowedIPs: createForm.allowedIPs ? 
          createForm.allowedIPs.split(',').map(ip => ip.trim()).filter(ip => ip) : 
          [],
        createdBy
      };
      
      const response = await fetch(`${API_BASE_URL}/api/v1/connector-keys`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create key');
      }
      
      await fetchKeys();
      setIsCreateDialogOpen(false);
      setCreateForm({ organizationId: '', buildingId: '', description: '', expiresAt: '', allowedIPs: '' });
      toast.success('Connector key created successfully!');
    } catch (error) {
      console.error('Failed to create key:', error);
      toast.error('Failed to create connector key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (key: string) => {
    try {
      setRevokingKey(key);
      const headers = await getAuthHeader();
      const res = await fetch(`${API_BASE_URL}/api/v1/connector-keys/${key}/revoke`, { 
        method: 'POST',
        headers,
      });
      
      if (!res.ok) {
        throw new Error('Failed to revoke key');
      }
      toast.success('Key revoked successfully.');
      await fetchKeys();
    } catch (error) {
      toast.error('Failed to revoke key.');
      console.error('Failed to revoke key:', error);
    } finally {
      setRevokingKey(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(text);
      toast.success('Key copied to clipboard!');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard.');
    }
  };

  const maskKey = (key: string) => {
    if (!key) return '';
    const prefix = key.slice(0, 8);
    const suffix = key.slice(-4);
    return `${prefix}...${suffix}`;
  };

  const getDisplayName = (uid: string) => {
    const userInfo = userInfoCache[uid];
    return userInfo?.displayName || uid;
  };

  const getOrganizationName = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || orgId;
  };

  return (
    <div className="space-y-6 pt-2">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg">
            <Key className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">Connector Keys Management</h1>
        </div>
        <p className="text-amber-400 font-medium ml-14">
          Generate and manage connector keys for local applications to access organization data securely.
        </p>
      </div>

      {/* Organization Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Organization</CardTitle>
          <CardDescription>
            Choose an organization to view and manage its associated connector keys.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger className="w-[350px]">
                <SelectValue placeholder="Select an organization..." />
              </SelectTrigger>
              <SelectContent>
                {loadingOrgs ? (
                  <SelectItem value="loading" disabled>Loading organizations...</SelectItem>
                ) : organizations.length === 0 ? (
                  <SelectItem value="no-orgs" disabled>No organizations available</SelectItem>
                ) : (
                  organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700">Create New Key</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>Create New Connector Key</DialogTitle>
                  <DialogDescription>
                    Fill in the details below to create a new connector key.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="org-select" className="text-left">Organization *</Label>
                    <Select 
                      value={createForm.organizationId} 
                      onValueChange={(value) => setCreateForm(prev => ({ ...prev, organizationId: value }))}
                    >
                      <SelectTrigger id="org-select" className="col-span-3">
                        <SelectValue placeholder="Select an organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingOrgs ? (
                          <SelectItem key="loading" value="loading" disabled>Loading...</SelectItem>
                        ) : (
                          organizations.map(org => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="expiresAt" className="text-left">Expires At (Optional)</Label>
                    <Input 
                      id="expiresAt"
                      type="date"
                      value={createForm.expiresAt}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="allowedIPs" className="text-left">Allowed IPs (Optional)</Label>
                    <Input 
                      id="allowedIPs"
                      type="text"
                      placeholder="192.168.1.1, 10.0.0.1"
                      value={createForm.allowedIPs}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, allowedIPs: e.target.value }))}
                      className="col-span-3"
                    />
                    <div className="col-span-3 col-start-2">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-emerald-600"> Leave empty to allow all IPs.</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        💡 <span className="font-medium">Note:</span> Use the device's local IP (not public internet IP) for network security.
                      </p>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleCreateKey}
                    disabled={!createForm.organizationId || creating}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {creating ? (
                      <>
                        <SvgSpinner size={16} />
                        <span className="ml-2">Creating...</span>
                      </>
                    ) : (
                      <>
                        <Key className="h-4 w-4 mr-2" />
                        Create Key
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
      
      {selectedOrgId && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Active Keys for {getOrganizationName(selectedOrgId)}</CardTitle>
              <CardDescription>
                These are the keys currently active for the selected organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingKeys ? (
                <div className="space-y-6">
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                  <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ) : keys.length === 0 ? (
                <div className="text-center text-muted-foreground py-10">
                  No connector keys found for this organization.
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-amber-100 to-orange-100 border-amber-200">
                        <TableHead className="text-amber-900 font-semibold">
                          <Button
                            variant="ghost"
                            onClick={() => handleSort('key')}
                            className="h-auto p-0 font-semibold hover:bg-amber-200 text-amber-900"
                          >
                            Connector Key {getSortIcon('key')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-amber-900 font-semibold">
                          <Button
                            variant="ghost"
                            onClick={() => handleSort('createdBy')}
                            className="h-auto p-0 font-semibold hover:bg-amber-200 text-amber-900"
                          >
                            Created By {getSortIcon('createdBy')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-amber-900 font-semibold">
                          <Button
                            variant="ghost"
                            onClick={() => handleSort('expiresAt')}
                            className="h-auto p-0 font-semibold hover:bg-amber-200 text-amber-900"
                          >
                            Expires At {getSortIcon('expiresAt')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-amber-900 font-semibold">Allowed IPs</TableHead>
                        <TableHead className="text-amber-900 font-semibold">
                          <Button
                            variant="ghost"
                            onClick={() => handleSort('revoked')}
                            className="h-auto p-0 font-semibold hover:bg-amber-200 text-amber-900"
                          >
                            Status {getSortIcon('revoked')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-amber-900 font-semibold text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {keys
                        .sort((a, b) => {
                          let aValue: any, bValue: any;
                          
                          switch (sortKey) {
                            case 'key':
                              aValue = a.key.toLowerCase();
                              bValue = b.key.toLowerCase();
                              break;
                            case 'description':
                              aValue = (a.description || '').toLowerCase();
                              bValue = (b.description || '').toLowerCase();
                              break;
                            case 'createdBy':
                              aValue = getDisplayName(a.createdBy).toLowerCase();
                              bValue = getDisplayName(b.createdBy).toLowerCase();
                              break;
                            case 'createdAt':
                              aValue = a.createdAt;
                              bValue = b.createdAt;
                              break;
                            case 'expiresAt':
                              aValue = a.expiresAt || '';
                              bValue = b.expiresAt || '';
                              break;
                            case 'revoked':
                              aValue = a.revoked;
                              bValue = b.revoked;
                              break;
                            default:
                              return 0;
                          }
                          
                          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                          return 0;
                        })
                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                        .map(k => (
                        <TableRow key={k.key}>
                          <TableCell className="font-mono text-xs">
                            <div className="flex items-center gap-2">
                              <span>{maskKey(k.key)}</span>
                              <Button variant="ghost" size="icon" onClick={() => copyToClipboard(k.key)} className="h-7 w-7">
                                {copiedKey === k.key ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{getDisplayName(k.createdBy)}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {k.expiresAt ? formatDate(k.expiresAt) : 'Never'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {k.allowedIPs && k.allowedIPs.length > 0 ? (
                              <div className="text-xs">
                                <div className="font-medium text-emerald-600">
                                  {k.allowedIPs.slice(0, 2).join(', ')}
                                  {k.allowedIPs.length > 2 && ` +${k.allowedIPs.length - 2} more`}
                                </div>
                                <div className="text-xs text-muted-foreground">Restricted</div>
                              </div>
                            ) : (
                              <div className="text-xs">
                                <span className="font-medium text-green-600">All IPs</span>
                                <div className="text-xs text-muted-foreground">Unrestricted</div>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={k.revoked ? 'destructive' : 'default'}>
                              {k.revoked ? 'Revoked' : 'Active'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {!k.revoked && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 border-red-600 hover:bg-red-50"
                                    disabled={revokingKey === k.key}
                                  >
                                    {revokingKey === k.key ? (
                                      <>
                                        <span className="mr-2 inline-block animate-spin">⏳</span>
                                        Revoking...
                                      </>
                                    ) : (
                                      'Revoke'
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently revoke the key. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel disabled={revokingKey === k.key}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleRevokeKey(k.key)}
                                      className="bg-red-600 hover:bg-red-700"
                                      disabled={revokingKey === k.key}
                                    >
                                      {revokingKey === k.key ? 'Revoking...' : 'Revoke'}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between space-x-2 py-4">
                      <div className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Precedente
                        </Button>
                        
                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <Button
                              key={page}
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(page)}
                              className="w-8 h-8 p-0"
                            >
                              {page}
                            </Button>
                          ))}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Successivo
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Received Connector Data</CardTitle>
              <CardDescription>
                Log of data batches received from connectors for {getOrganizationName(selectedOrgId)}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {receivedData.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No connector data has been received for this organization yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source File</TableHead>
                      <TableHead>Building</TableHead>
                      <TableHead>Table</TableHead>
                      <TableHead>Records</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receivedData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.sourceFile || '-'}</TableCell>
                        <TableCell>{item.buildingId || '-'}</TableCell>
                        <TableCell>{item.table || '-'}</TableCell>
                        <TableCell>{item.recordCount ?? '-'}</TableCell>
                        <TableCell>{formatDate(item.timestamp || item.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ConnectorKeysPage;